import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import SupplierManagementShell from "./SupplierManagementShell";
import RegisteredList from "./components/RegisteredList";
import InvitationsList from "./components/InvitationsList";
import SupplierDetailsModal from "./components/SupplierDetailsModal";
import InvitationDetailsModal from "./components/InvitationDetailsModal";
import CreateInvitationModal from "./components/CreateInvitationModal";
import ResendInvitationModal from "./components/ResendInvitationModal";
import useSupplierData from "./hooks/useSupplierData";
import "./supplierManagementTables.css";

const VIEW_REGISTERED = "registered";
const VIEW_INVITATIONS = "invitations";

/**
 * Supplier management page: registered-suppliers list + invitations list
 * with tabs, plus modals for details, invite create, and resend. Owns the
 * top-level view/modal state; the two list panels handle their own
 * search/filter internally.
 */
const RegisterdSupplierManagement = () => {
  const location = useLocation();
  const {
    supplierList,
    invitationList,
    suppliersLoading,
    invitationsLoading,
    getSuppliers,
    getInvitations,
  } = useSupplierData();

  const [activeView, setActiveView] = useState(VIEW_REGISTERED);
  const [openMenuId, setOpenMenuId] = useState(null);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsSupplierId, setDetailsSupplierId] = useState(null);

  const [invitationDetailsOpen, setInvitationDetailsOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);

  const [resendOpen, setResendOpen] = useState(false);
  const [resendId, setResendId] = useState(null);

  const openDetails = (supplierId) => {
    if (!supplierId) return;
    setDetailsSupplierId(supplierId);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsSupplierId(null);
  };

  const openInvitationDetails = (row) => {
    setSelectedInvitation(row);
    setInvitationDetailsOpen(true);
  };

  const closeInvitationDetails = () => {
    setInvitationDetailsOpen(false);
    setSelectedInvitation(null);
  };

  const openResend = (supplierId) => {
    if (!supplierId) return;
    setResendId(supplierId);
    setResendOpen(true);
  };

  const closeResend = () => {
    setResendOpen(false);
    setResendId(null);
  };

  const switchView = (view) => {
    setActiveView(view);
    setOpenMenuId(null);
  };

  const handleInviteCreated = ({ switchToInvitations }) => {
    getSuppliers();
    getInvitations();
    if (switchToInvitations) setActiveView(VIEW_INVITATIONS);
  };

  const totalRegistered = supplierList?.length ?? 0;
  const totalInvitations = invitationList?.length ?? 0;
  const isInvitationsView = activeView === VIEW_INVITATIONS;
  const displayCount = isInvitationsView ? totalInvitations : totalRegistered;

  return (
    <SupplierManagementShell
      pathname={location?.pathname}
      pageTitle="Supplier management"
      totalCount={displayCount}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 12,
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button
            type="button"
            className={
              activeView === VIEW_REGISTERED ? "kpi-btn-primary" : "kpi-btn-secondary"
            }
            onClick={() => switchView(VIEW_REGISTERED)}
          >
            Registered suppliers
            {totalRegistered > 0 ? ` (${totalRegistered})` : ""}
          </button>
          <button
            type="button"
            className={
              activeView === VIEW_INVITATIONS ? "kpi-btn-primary" : "kpi-btn-secondary"
            }
            onClick={() => switchView(VIEW_INVITATIONS)}
          >
            Invitations
            {totalInvitations > 0 ? ` (${totalInvitations})` : ""}
          </button>
        </div>
        <button
          type="button"
          className="kpi-btn-primary"
          onClick={() => setCreateOpen(true)}
        >
          + Invite supplier
        </button>
      </div>

      {isInvitationsView ? (
        <InvitationsList
          rows={invitationList}
          loading={invitationsLoading}
          openMenuId={openMenuId}
          setOpenMenuId={setOpenMenuId}
          onOpenInvitationDetails={openInvitationDetails}
          onOpenResend={openResend}
          onInvite={() => setCreateOpen(true)}
        />
      ) : (
        <RegisteredList
          rows={supplierList}
          loading={suppliersLoading}
          openMenuId={openMenuId}
          setOpenMenuId={setOpenMenuId}
          onOpenDetails={openDetails}
        />
      )}

      <InvitationDetailsModal
        show={invitationDetailsOpen}
        invitation={selectedInvitation}
        onClose={closeInvitationDetails}
        onResend={openResend}
      />

      <CreateInvitationModal
        show={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleInviteCreated}
      />

      <SupplierDetailsModal
        show={detailsOpen}
        supplierId={detailsSupplierId}
        onClose={closeDetails}
      />

      <ResendInvitationModal
        show={resendOpen}
        supplierId={resendId}
        onClose={closeResend}
        onSuccess={() => {
          getSuppliers();
          getInvitations();
        }}
      />
    </SupplierManagementShell>
  );
};

export default RegisterdSupplierManagement;
